import {
  postBackendDownloadCancelDownload,
  postBackendDownloadGetDownloadList,
} from '@/services/yizhe-web/gonggongfenlei';
import {
  postBackendImportTaskCancelImportTask,
  postBackendImportTaskGetImportTaskDetail,
  postBackendImportTaskGetImportTaskList,
  postBackendImportTaskGetImportTaskRows,
} from '@/services/yizhe-web/shujudaoru';
import type { ImportNotifyPayload } from '@/utils/importExportNotify';
import { YIZHE_IMPORT_NOTIFY_EVENT } from '@/utils/importExportNotify';
import { DownloadOutlined } from '@ant-design/icons';
import { ProTable } from '@ant-design/pro-components';
import { Alert, Button, Image, message, Modal, Progress, Space, Tabs } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type ImportListItem = NonNullable<NonNullable<API.GetImportTaskListResult['data']>['list']>[number];

const ImportResultModalContent: React.FC<{ taskId: number }> = ({ taskId }) => {
  const [detail, setDetail] = useState<API.GetImportTaskDetailResult['data']>();
  const [tab, setTab] = useState<'fail' | 'success'>('fail');
  const actionRef = useRef<any>(null);

  useEffect(() => {
    setDetail(undefined);
    (async () => {
      const { code, data } = await postBackendImportTaskGetImportTaskDetail({ task_id: taskId });
      if (code === '200' && data) {
        setDetail(data);
        setTab((data.fail_count ?? 0) > 0 ? 'fail' : 'success');
      }
    })();
  }, [taskId]);

  /** 与详情里默认 Tab 规则一致后再拉行数据，避免先用「失败」row_status 请求、与 fail_count 默认 Tab 不一致 */
  useEffect(() => {
    if (detail) {
      actionRef.current?.reload();
    }
  }, [detail]);

  const handleDownload = () => {
    const url = tab === 'fail' ? detail?.fail_file : detail?.success_file;
    if (url) {
      window.open(url);
    } else {
      message.warning('暂无可下载文件');
    }
  };

  const failCount = detail?.fail_count ?? 0;
  const successCount = detail?.success_count ?? 0;

  return (
    <div>
      <Alert
        type="warning"
        showIcon={false}
        style={{ marginBottom: 16, background: '#fff7e6', border: '1px solid #ffd591' }}
        message={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <Space size={16}>
              <span style={{ color: '#52c41a' }}>成功 {successCount} 条</span>
              <span style={{ color: '#ff4d4f' }}>失败 {failCount} 条</span>
            </Space>
            {failCount > 0 ? (
              <span style={{ color: '#fa8c16' }}>以下为导入失败的样板，请重新导入！</span>
            ) : null}
          </div>
        }
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <Tabs
          activeKey={tab}
          onChange={(k) => {
            setTab(k as 'fail' | 'success');
            actionRef.current?.reload();
          }}
          items={[
            {
              key: 'fail',
              label: (
                <span style={{ color: tab === 'fail' ? '#ff4d4f' : undefined }}>
                  导入失败 ({failCount})
                </span>
              ),
            },
            {
              key: 'success',
              label: (
                <span style={{ color: tab === 'success' ? '#52c41a' : undefined }}>
                  导入成功 ({successCount})
                </span>
              ),
            },
          ]}
        />
        <Button icon={<DownloadOutlined />} onClick={handleDownload}>
          下载数据
        </Button>
      </div>
      <ProTable<NonNullable<API.GetImportTaskRowsResult['data']>['list'][number]>
        actionRef={actionRef}
        columns={[
          {
            title: '图片',
            dataIndex: 'template_images',
            width: 72,
            align: 'center',
            search: false,
            render: (_, r) => {
              const src = r.template_images?.[0];
              return src ? (
                <Image src={src} width={48} height={48} style={{ objectFit: 'cover' }} />
              ) : (
                '-'
              );
            },
          },
          {
            title: '款号',
            dataIndex: 'template_no',
            align: 'center',
            render: (v) => v || '-',
          },
          {
            title: '款名',
            dataIndex: 'template_name',
            align: 'center',
            render: (v) => v || '-',
          },
          {
            title: '颜色',
            dataIndex: 'color',
            align: 'center',
            render: (v) => v || '-',
          },
          {
            title: '尺码',
            dataIndex: 'size',
            align: 'center',
            render: (v) => v || '-',
          },
          ...(tab === 'fail'
            ? [
                {
                  title: '失败原因',
                  dataIndex: 'fail_reason',
                  align: 'center' as const,
                  render: (_: unknown, r: { fail_reason?: string }) => (
                    <span style={{ color: '#ff4d4f' }}>{r.fail_reason || '-'}</span>
                  ),
                },
              ]
            : []),
        ]}
        search={false}
        options={false}
        rowKey="id"
        request={async (params) => {
          if (!detail) {
            return { data: [], total: 0, success: true };
          }
          const row_status: 1 | 2 = tab === 'fail' ? 2 : 1;
          const { code, data } = await postBackendImportTaskGetImportTaskRows({
            task_id: taskId,
            row_status,
            page: params.current,
            page_size: params.pageSize,
          });
          if (code !== '200') {
            return { data: [], success: false, total: 0 };
          }
          return {
            data: data?.list ?? [],
            total: data?.total ?? 0,
            success: true,
          };
        }}
        pagination={{ defaultPageSize: 10 }}
        bordered
        size="small"
      />
    </div>
  );
};

/** 打开指定任务的导入结果明细（供消息通知「查看详情」等调用） */
export function showImportResultModal(taskId: number) {
  Modal.info({
    icon: null,
    width: 960,
    title: '导入结果',
    okText: '确定',
    closable: true,
    maskClosable: true,
    content: <ImportResultModalContent taskId={taskId} />,
  });
}

const ExportImportCenterInner: React.FC = () => {
  const [activeKey, setActiveKey] = useState<string>('export');
  const exportActionRef = useRef<any>(null);
  const importActionRef = useRef<any>(null);
  const [importProgressById, setImportProgressById] = useState<Record<number, number>>({});

  const mergeImportNotify = useCallback((payload: ImportNotifyPayload) => {
    const c = payload?.content;
    if (c?.task_id != null && typeof c.import_progress === 'number') {
      setImportProgressById((prev) => ({
        ...prev,
        [c.task_id!]: c.import_progress!,
      }));
    }
    importActionRef.current?.reload?.();
    exportActionRef.current?.reload?.();
  }, []);

  useEffect(() => {
    const onNotify = (e: Event) => {
      const ce = e as CustomEvent<ImportNotifyPayload>;
      mergeImportNotify(ce.detail);
    };
    window.addEventListener(YIZHE_IMPORT_NOTIFY_EVENT, onNotify);
    return () => window.removeEventListener(YIZHE_IMPORT_NOTIFY_EVENT, onNotify);
  }, [mergeImportNotify]);

  const handleCancelExport = (record: any) => {
    Modal.confirm({
      title: '取消导出',
      content: '确定取消导出？',
      onOk: async () => {
        const { code } = await postBackendDownloadCancelDownload({ id: record.id });
        if (code === '200') {
          message.success('取消成功');
          exportActionRef.current?.reload?.();
        }
      },
    });
  };

  const handleOkExport = (record: any) => {
    window.open(record.file_url);
  };

  const exportColumns = useMemo(
    () => [
      {
        title: '导出内容',
        dataIndex: 'title',
        key: 'title',
        align: 'center' as const,
      },
      {
        title: '筛选时间',
        dataIndex: 'time_range',
        key: 'time_range',
        align: 'center' as const,
      },
      {
        title: '状态',
        dataIndex: 'down_status',
        key: 'down_status',
        width: 140,
        align: 'center' as const,
        render: (_: any, record: any) => {
          return (
            <>
              {record.down_status === 0 ? (
                <Progress percent={+record.progress} status="active" />
              ) : (
                <span>{record.down_status_desc}</span>
              )}
            </>
          );
        },
      },
      {
        title: '操作时间',
        dataIndex: 'created_at',
        key: 'created_at',
        align: 'center' as const,
      },
      {
        title: '操作',
        dataIndex: 'option',
        key: 'option',
        align: 'center' as const,
        render: (_: any, record: any) => {
          return (
            <>
              {record.down_status === 0 ? (
                <a
                  onClick={() => {
                    handleCancelExport(record);
                  }}
                >
                  取消导出
                </a>
              ) : null}
              {record.can_download === 1 ? (
                <a
                  onClick={() => {
                    handleOkExport(record);
                  }}
                >
                  下载
                </a>
              ) : null}
            </>
          );
        },
      },
    ],
    [],
  );

  const handleCancelImport = useCallback((record: ImportListItem) => {
    Modal.confirm({
      title: '取消导入',
      content: '确定取消该导入任务？',
      onOk: async () => {
        const { code, msg } = await postBackendImportTaskCancelImportTask({ task_id: record.id });
        if (code === '200') {
          message.success('已取消导入');
          importActionRef.current?.reload?.();
        } else {
          message.error(msg || '取消失败');
        }
      },
    });
  }, []);

  const handleImportDownload = useCallback(async (record: ImportListItem) => {
    const { code, data } = await postBackendImportTaskGetImportTaskDetail({ task_id: record.id });
    if (code === '200' && data?.success_file) {
      window.open(data.success_file);
    } else {
      message.warning('暂无可下载文件');
    }
  }, []);

  const importColumns = useMemo(
    () => [
      {
        title: '导入文件名',
        dataIndex: 'display_name',
        key: 'display_name',
        align: 'center' as const,
      },
      {
        title: '导入类型',
        dataIndex: 'import_type_name',
        key: 'import_type_name',
        align: 'center' as const,
      },
      {
        title: '状态',
        dataIndex: 'status',
        key: 'status',
        width: 160,
        align: 'center' as const,
        render: (_: unknown, record: ImportListItem) => {
          const merged =
            record.id != null && importProgressById[record.id] != null
              ? importProgressById[record.id]
              : Number(record.import_progress) || 0;
          if (record.status === 2 || record.status === 1) {
            return <Progress percent={Math.min(100, merged)} status="active" />;
          }
          if (record.status === 4) {
            return <span style={{ color: '#fa8c16' }}>导入失败</span>;
          }
          if (record.status === 3) {
            return <span style={{ color: '#52c41a' }}>已完成</span>;
          }
          if (record.status === 5) {
            return <span style={{ color: '#999' }}>{record.status_name || '已取消'}</span>;
          }
          return <span>{record.status_name || '-'}</span>;
        },
      },
      {
        title: '操作时间',
        dataIndex: 'created_at',
        key: 'created_at',
        align: 'center' as const,
      },
      {
        title: '操作',
        key: 'action',
        align: 'center' as const,
        render: (_: unknown, record: ImportListItem) => {
          if (record.status === 1 || record.status === 2) {
            return <a onClick={() => handleCancelImport(record)}>取消导入</a>;
          } else {
            return (
              <a
                onClick={() => {
                  showImportResultModal(record.id);
                }}
              >
                明细
              </a>
            );
          }
        },
      },
    ],
    [importProgressById, handleCancelImport],
  );

  return (
    <Tabs
      activeKey={activeKey}
      onChange={setActiveKey}
      items={[
        {
          key: 'export',
          label: '导出历史',
          children: (
            <ProTable<NonNullable<NonNullable<API.GetDownloadListResult['data']>['list']>[number]>
              actionRef={exportActionRef}
              columns={exportColumns}
              polling={activeKey === 'export' ? 2000 : undefined}
              search={false}
              toolBarRender={false}
              rowKey="id"
              request={async (params) => {
                const { data }: any = await postBackendDownloadGetDownloadList({
                  ...params,
                  page: params?.current,
                });
                return {
                  data: data?.list,
                  total: data?.total,
                };
              }}
              bordered
              pagination={{
                defaultPageSize: 10,
              }}
            />
          ),
        },
        {
          key: 'import',
          label: '导入历史',
          children: (
            <ProTable<ImportListItem>
              actionRef={importActionRef}
              columns={importColumns}
              polling={activeKey === 'import' ? 2000 : undefined}
              search={false}
              toolBarRender={false}
              rowKey="id"
              request={async (params) => {
                const { code, data } = await postBackendImportTaskGetImportTaskList({
                  page: params.current,
                  page_size: params.pageSize,
                });
                if (code !== '200') {
                  return { data: [], total: 0, success: false };
                }
                return {
                  data: data?.list ?? [],
                  total: data?.total ?? 0,
                  success: true,
                };
              }}
              bordered
              pagination={{
                defaultPageSize: 10,
              }}
            />
          ),
        },
      ]}
    />
  );
};

export const showModalCommonExport = () => {
  Modal.success({
    icon: null,
    title: '导出/入',
    width: 800,
    closable: true,
    okText: '关闭',
    okButtonProps: { type: 'default' },
    autoFocusButton: null,
    content: <ExportImportCenterInner />,
  });
};
